import videojs, { VideoJsPlayer } from 'video.js';

import { Event } from '@src/types/event';
import logger from '@src/utils/logger';
import {
  getIsPartialElementInViewport,
  getIsWholeElementInViewport,
} from '@src/utils/measure';
import { attachPluginToPlayer } from '@src/utils/plugin';

const Plugin = videojs.getPlugin('plugin');

export const PLUGIN_NAME = 'controller';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Options {}

export enum PauseAdMode {
  RESET_IMA = 'reset_ima',
  PAUSE_AD = 'pause_ad',
}

type State = {
  lastResetImaCount: number;
};

interface Controller {
  setState: (state: Partial<State>) => void;
  state: State;
}

class Controller extends Plugin {
  static defaultState: State;
  constructor(player: VideoJsPlayer, options?: Options) {
    super(player, options);

    attachPluginToPlayer(this, player);

    this.setState({
      lastResetImaCount: player.contextPlugin.state.resetImaCount || 0,
    });
    this.patchAutoplayOnEnterViewportLately();
  }

  public pauseVideoOrAd = (options?: {
    pauseAdMode?: PauseAdMode | `${PauseAdMode}` | false;
    pauseVideoMode?: boolean;
    isForcePauseVideo?: boolean;
  }) => {
    const isAdPlaying = this.player.adsManagerPlugin?.isAdPlaying();
    const isInAdBreak = this.player.adsManagerPlugin?.isInAdBreak();

    const isAdPlayingOrInAdBreak = isAdPlaying || isInAdBreak;

    const {
      pauseAdMode = PauseAdMode.PAUSE_AD,
      pauseVideoMode = true,
      isForcePauseVideo = false,
    } = options ?? {};

    if (isAdPlayingOrInAdBreak) {
      if (pauseAdMode === PauseAdMode.RESET_IMA) {
        this.player?.adsManagerPlugin?.resetIma?.();

        if (
          this.player.eventManagerPlugin?.playStateNormalizer?.state
            ?.isAllowNextPauseToTrigger
        ) {
          this.player.eventManagerPlugin?.playStateNormalizer?.setState?.({
            isAllowNextPauseToTrigger: false,
          });
        }

        this.player?.pause?.(); // without this line, the player will resume video after resetting ima
      } else if (pauseAdMode === PauseAdMode.PAUSE_AD) {
        this.player?.adsManagerPlugin?.getAdsManager()?.pause?.();
      }
    }

    if (
      (!isAdPlayingOrInAdBreak && pauseVideoMode && !this.player.paused()) ||
      isForcePauseVideo
    ) {
      this.player.contextPlugin.setState({
        isControllerTriggeredPlay: true, // so that next click-to-play or auto-resume will fire Event.RESUME
        isControllerTriggeredPause: !this.player.paused(), // so that the pause() below will fire Event.PAUSE
      });

      this.player.pause();
    }
  };

  public handleRequestAdsOnClickToPlay() {
    // deregister previous listener in case of double register
    this.player.off(Event.CLICKTOPLAY, this.requestAds);
    this.player.one(Event.CLICKTOPLAY, this.requestAds);
  }

  public requestAds = (options?: { force?: boolean }) => {
    const { force = false } = options ?? {};

    // deregister previous listener in case of requesting ads again on next click
    this.player.off(Event.CLICKTOPLAY, this.requestAds);
    const contextState = this.player.contextPlugin.state ?? {};

    const isResetImaCountChanged =
      this.state.lastResetImaCount !== contextState.resetImaCount;

    this.setState({ lastResetImaCount: contextState.resetImaCount });

    if (contextState.blockAd) return false;

    if (
      force ||
      (!contextState.isCompleteAllAds && isResetImaCountChanged) ||
      !contextState.isImaRequestedAd
    ) {
      this.player?.adsManagerPlugin?.requestImaAds?.();
      return true;
    }

    return false;
  };

  public getIsAutoplay() {
    const autoplayConfig = this.player.autoplay?.();
    return [true, 'any', 'muted', 'play'].includes(autoplayConfig);
  }

  public requestAdsOrPlay = () => {
    // should no need to trigger play again if has adsManagerPlugin.requestImaAds()
    // because IMA will resume playing automatically
    if (!this.requestAds() && this.player.paused()) {
      this.player.contextPlugin.setState({ isControllerTriggeredPlay: true });

      return this.player.play()?.catch?.((err) => {
        const autoplayConfig = this.player.autoplay();
        logger.warn(err, 'Fail to requestAdsOrPlay when attempt to play()');

        if (this.getIsAutoplay()) {
          this.player.autoplay(autoplayConfig);
        }
      });
    }
  };

  // https://github.com/videojs/video.js/blob/v6.13.0/src/js/control-bar/mute-toggle.js#L56
  public unmuteAndSetToLastVolume = () => {
    const lastVolume = this.player.lastVolume_();
    const volumeToSet = lastVolume < 0.1 ? 0.1 : lastVolume;

    this.player.volume(volumeToSet);
    this.player.muted(false);
  };

  public unmuteAndSetToLastVolumeIfUserUntouchBefore = () => {
    if (this.player.contextPlugin.state.userVolumeChangeCount > 0) return;

    this.unmuteAndSetToLastVolume();
  };

  public handleSetToLastVolumeOnClickToPlay() {
    // deregister previous listener in cause double register
    this.player.off(
      Event.CLICKTOPLAY,
      this.unmuteAndSetToLastVolumeIfUserUntouchBefore
    );
    this.player.one(
      Event.CLICKTOPLAY,
      this.unmuteAndSetToLastVolumeIfUserUntouchBefore
    );
  }

  private attemptProgrammaticAutoplay = async () => {
    const { clickToPauseCount } = this.player.contextPlugin?.state ?? {};
    const isPaused = this.player.paused();
    const isAutoplay = this.getIsAutoplay();

    // 3 actions to do
    if (isAutoplay && isPaused && !clickToPauseCount) {
      const autoplayConfig = this.player.autoplay();

      // 1) mute the player if it should be but is not muted
      if (
        autoplayConfig === 'muted' &&
        !this.player.contextPlugin.state.userVolumeChangeCount &&
        !this.player.contextPlugin.state.adVolumeChangeCount &&
        !this.player.contextPlugin.state.clickToPlayCount &&
        !this.player.muted()
      ) {
        this.player.muted(true);
      }

      try {
        // 2) play the video; should not use player.autoplay(autoplayConfig) here because
        // there is additional logic restoring the muted state if it fail to autoplay
        await this.player.play();
      } catch (err) {
        logger.warn(err, 'Fail to handle autoplay programmatically');
      }

      // 3) mute the player if the player.manualAutoplay_ in video.js restore the muted state
      // due to pause caused by contrib-ads / videojs-ima
      if (
        autoplayConfig === 'muted' &&
        !this.player.contextPlugin.state.userVolumeChangeCount &&
        !this.player.contextPlugin.state.adVolumeChangeCount &&
        !this.player.contextPlugin.state.clickToPlayCount &&
        !this.player.muted()
      ) {
        this.player.muted(true);
      }
    }

    // TODO handle autoplay="any" by can-autoplay
  };

  // need to patch as in video.js 7+, player.play_ in player.manualAutoplay_
  // will loop back to player.play_ on loadstart instead of player.play
  // player.play_ require a check for truthy changingSrc_ which is not set properly
  // when user switch out the tab initially (GROWTH-3713)
  private patchAutoplayOnEnterViewportLately() {
    this.player.ready(() => {
      const handleVisibilityChange = () => {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );

        if (!document.hidden) {
          const isAdPlaying = this.player.adsManagerPlugin?.isAdPlaying();
          const isInAdBreak = this.player.adsManagerPlugin?.isInAdBreak();

          if (!isAdPlaying && !isInAdBreak && this.player.paused()) {
            this.attemptProgrammaticAutoplay();
          }
        }
      };

      if (document.hidden === true) {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }
    });
  }

  public getIsPartialPlayerInViewport() {
    return getIsPartialElementInViewport(this.player.contentEl());
  }

  public getIsWholePlayerInViewport() {
    return getIsWholeElementInViewport(this.player.contentEl());
  }
}

videojs.registerPlugin(PLUGIN_NAME, Controller);

Controller.defaultState = {
  lastResetImaCount: 0,
};

export type ControllerFactoryFn = (options?: Options) => Controller;

export default Controller;
